//-------------------------------------------------------
//   Copyright (c) 2024 Templa Computer Systems Ltd
//
//   App:        EmployeePortal.UI
//   Object:     DataForm
//   Details:    DataForm store
//
// Log      Date   By  Scan Description
// -----  -------- --- ---- ------------------------------
// WI3713 24/01/24 JC		New
//-------------------------------------------------------

import { fetchActions } from "../FetchWrapper";
import { userActions } from './Authentication';
import { dataFormDataTypes } from "../constants/DataFormDataTypes";

const dataFormRequestType = 'DATAFORM_REQUEST';
const dataFormSuccessType = 'DATAFORM_SUCCESS';
const dataFormFailureType = 'DATAFORM_FAILURE';
const dataFormUpdateChangedAnswerType = "DATAFORM_UPDATECHANGEDANSWER";
const dataFormSaveType = 'DATAFORM_SAVE';
const dataFormSaveFailureType = "DATAFORM_SAVEFAILURE";
const dataFormValidatedErrorType = 'DATAFORM_VALIDATEDERROR';
const dataFormRemoveMessageBoxTextType = 'DATAFORM_REMOVEMESSAGEBOXTEXT';

const initialState = {
	dataForm: undefined,
	changedAnswers: {},
	validatedAnswers: {},
	ownerId: 0,
	ownerType: 0,
	isLoading: false,
	canRequest: true,
	error: undefined,
	validationError: undefined,
	hasSaveError: false,
	messageBoxText: undefined
};

export const dataFormActions = {
	getDataForm,
	updateAnswer,
	save,
	removeMessageBoxText
};

function getDataForm(ownerId, ownerType) {
	return (dispatch, getState) => {

		if (!getState().clientConfiguration.isLoaded)
			return;

		if (!getState().clientConfiguration.config.endpoint && !getState().authentication.user)
			return;

		dispatch(_request());

		const url = getState().clientConfiguration.config.endpoint + "/EmployeeDataForms/Get";
		const query = `?ownerId=${ownerId}&ownerType=${ownerType}`;
        const ticket = getState().authentication.user.ticket;


		fetchActions.get(url + query, ticket)
			.then(
				response => {
					const dataForm = response.data;
					datesToHtml(dataForm);
					dispatch(_success(dataForm, ownerId, ownerType));
				},
				error => {
					if (error.sessionExpired) {
						dispatch(userActions.sessionExpired());
					}
					else {
						var message = `Could not get data form: ${error.message}`;
						dispatch(_failure(message));
					}
				}
			);
	};

	function datesToHtml(dataForm) {
		return dataForm.dataFormItems.map((item, i) => {
			if (item.dataType === dataFormDataTypes.date_and_time) // DateTime
				item.answer = item.answer.replace(/\//g, "-");
			return item;
		});
	}

	function _request() { return { type: dataFormRequestType }; }
	function _success(dataForm, ownerId, ownerType) { return { type: dataFormSuccessType, dataForm, ownerId, ownerType }; }
	function _failure(error) { return { type: dataFormFailureType, error }; }
}

function updateAnswer(item) {
	return (dispatch, getState) => {

		//declare new changed answers object
		let newChangedAnswers = {};

		//determine if we've found a match
		let bMatchFound = false;

		//get state objects
		const { dataForm, changedAnswers, validatedAnswers } = getState().dataForm;

		//go through each changed answer
		Object.keys(changedAnswers).forEach(function (lineNumber) {

			// if the lineNumber matches
			if (lineNumber.toString() === item.lineNumber.toString()) {

				//flag as match found
				bMatchFound = true;

				//if changed
				if (item.isChanged) {
					// if changed, add to new changed answers
					newChangedAnswers[lineNumber] = { answer: item.answer};
				}
				else {
					//do nothing as will not be copied over, i.e. deleted
				}
			} else {
				//doesn't match so copy over anyway
				newChangedAnswers[lineNumber] = { ...changedAnswers[lineNumber] };
			}
		});

		//not found & item is changed so add
		if (!bMatchFound && item.isChanged) {
			newChangedAnswers[item.lineNumber] = { answer: item.answer};
		}

		//validate - specifically for this item's line number
		let validateAnswersWrapper = { validatedAnswers: {}, validationError: undefined, messageBoxText: undefined };
		validate(false, dataForm, newChangedAnswers, validatedAnswers, validateAnswersWrapper, item.lineNumber);

		// apply new list back to state
		dispatch(_updateChangedAnswer(newChangedAnswers, validateAnswersWrapper.validatedAnswers, validateAnswersWrapper.validationError));
	};

	function _updateChangedAnswer(changedAnswers, validatedAnswers, validationError) {
		return { type: dataFormUpdateChangedAnswerType, changedAnswers, validatedAnswers, validationError };
	}
}

function save(complete) {
	return (dispatch, getState) => {
		if (!getState().clientConfiguration.config.endpoint)
			return;

		// get changed answers from state
		let { changedAnswers, validatedAnswers, dataForm } = getState().dataForm;

		//get data form details
		let { id, dataFormOwnerType } = dataForm;

		// if any changes in state, or complete
		if (changedAnswers.length !== 0 || complete) {

			//validate
			let validateAnswersWrapper = { validatedAnswers: {}, validationError: undefined, messageBoxText: undefined };
			if (validate(complete, dataForm, changedAnswers, validatedAnswers, validateAnswersWrapper, 0)) {

				dispatch(_saving(validateAnswersWrapper.validatedAnswers));

				//let hasImage = getState().myDetails.imageFile !== null;

				//set URL and auth ticket
				const url = getState().clientConfiguration.config.endpoint + "/EmployeeDataForms/Put";
				const query = `?ownerId=${id}&ownerType=${dataFormOwnerType}&complete=${complete}`;
				const ticket = getState().authentication.user.ticket;
				var data = [];

				//build 'data' array from list of changedAnswer
				Object.keys(changedAnswers).forEach(function (lineNumber) {

					//get the answer
					let answer = changedAnswers[lineNumber].answer;
					//add to the data
					data.push({ key: lineNumber, value: answer });
				});

				//post to the API
				fetchActions.post(url + query, ticket, data)
					.then(
						response => {
							if (response.data) {
								var message = `Error saving data form: ${response.data}`;
								dispatch(_failure(message));
							}
							setTimeout(function () {
								dispatch(dataFormActions.getDataForm(id, dataFormOwnerType));
							}, 500);
						},
						error => {
							if (error.sessionExpired) {
								dispatch(userActions.sessionExpired());
							}
							else {
								var message = `Could not save data form: ${error.message}`;
								dispatch(_failure(message));
							}
						}
					);
			} else {
				dispatch(_validate(validateAnswersWrapper.validatedAnswers, validateAnswersWrapper.validationError, validateAnswersWrapper.messageBoxText));
			}//validate end
		}//can save/complete end
	};

	function _saving(validatedAnswers) { return { type: dataFormSaveType, validatedAnswers }; }
	function _failure(error) { return { type: dataFormSaveFailureType, error }; }
	function _validate(validatedAnswers, validationError, messageBoxText) {
		return { type: dataFormValidatedErrorType, validatedAnswers, validationError, messageBoxText };
	}
}

function removeMessageBoxText() {
	return (dispatch) => {
		dispatch(_removeMessageBoxText());
	};
	function _removeMessageBoxText() { return { type: dataFormRemoveMessageBoxTextType }; }
}

function validate(forComplete, dataForm, changedAnswers, validatedAnswers, validatedAnswersWrapper, specificLineNumber) {
	//---------------------------------------------
	//validate a data form for save / complete
	//---------------------------------------------
	//build a new validated answers & error
	let newValidatedAnswers = {};
	let newValidationError = undefined;
	let newMessageBoxText = undefined;
	
	//iterate through each data form item
	dataForm.dataFormItems.forEach(function (dataFormItem) {
		//image or signature?
		let bImageOrSignature = (dataFormItem.dataType === dataFormDataTypes.image || dataFormItem.dataType === dataFormDataTypes.signature_box);
		
		//if looking for a specific line number which isn't this item, then just copy over the validated answer
		let bValidate = true;
		let bValidateMandatory = false;
		if (specificLineNumber !== 0) {
			if (dataFormItem.lineNumber !== specificLineNumber) {
				if (dataFormItem.lineNumber in validatedAnswers) {
					newValidatedAnswers[dataFormItem.lineNumber] = validatedAnswers[dataFormItem.lineNumber];
				}
				bValidate = false;
			} else {
				//if for the specific entry, validate mandatory if there's a mandatory error already
				if (dataFormItem.lineNumber in validatedAnswers) {
					bValidateMandatory = validatedAnswers[dataFormItem.lineNumber].hasMandatoryError;
				}				
			}
		}

		//i okay to validate
		if (bValidate) {

			//find the answer & whether there's document content
			let answer = dataFormItem.answer;
			let hasDocumentContent = dataFormItem.hasDocumentContent;
			Object.keys(changedAnswers).forEach(function (lineNumber) {
				if (lineNumber.toString() === dataFormItem.lineNumber.toString()) {
					//got a changed answer?
					answer = changedAnswers[lineNumber].answer;

					//if the item allows image upload or is a signature, get the updated hasDocumentContent
					if (bImageOrSignature && (dataFormItem.allowsImageUpload || dataFormItem.dataType === dataFormDataTypes.signature_box)) {
						hasDocumentContent = answer.length > 0;
					}
				}
			});

			//mandatory - for complete only or must validate mandatory - either no answer or image/signature and no document content
			if (dataFormItem.isMandatory && (forComplete || bValidateMandatory) && (
				(!bImageOrSignature && answer.length === 0) || (bImageOrSignature && !hasDocumentContent)
			)) {
				//get the correct message
				let messageSuffix = ": Mandatory answer missing";
				if (dataFormItem.dataType === dataFormDataTypes.image) {
					messageSuffix = ": Mandatory image missing";
				} else if (dataFormItem.dataType === dataFormDataTypes.signature_box) {
					messageSuffix = ": Mandatory signature missing";
				}

				newValidatedAnswers[dataFormItem.lineNumber] = {
					hasError: true,
					hasMandatoryError: true,
					message: dataFormItem.lineText + messageSuffix
				};
			}

			//validate - only for certain data types
			switch (dataFormItem.dataType) {
				case dataFormDataTypes.numeric_0dp:
				case dataFormDataTypes.numeric_1dp:
				case dataFormDataTypes.numeric_2dp:
				case dataFormDataTypes.numeric_3dp:
				case dataFormDataTypes.numeric_4dp:
					//if there's a min or a max value, check we've got an entered value and that it fits in the min/max, treating blank answer as 0
					let handledAnwser = parseFloat(answer.length === 0 ? 0 : answer);
					if (dataFormItem.maxValue !== 0 &&
						(handledAnwser <= dataFormItem.minValue || handledAnwser >= dataFormItem.maxValue)) {
						newValidatedAnswers[dataFormItem.lineNumber] = {
							hasError: true,
							hasMandatoryError: false,
							message: dataFormItem.lineText + ": value must be between " + dataFormItem.minValue + " and " + dataFormItem.maxValue
						};						
					}
					break;
				default:
					break;
			}
		}
	});

	//build up the new validation error based on the validated answers
	Object.keys(newValidatedAnswers).forEach(function (lineNumber) {
		newValidationError = (newValidationError === undefined ? "" : newValidationError + "\n") + newValidatedAnswers[lineNumber].message;
	})	

	//return no validated answers
	let bOK = Object.keys(newValidatedAnswers).length === 0;

	//add message box text if no specific line number
	if (!bOK && specificLineNumber === 0) {
		newMessageBoxText = "Could not " + (!forComplete ? "save" : "complete")  + " due to errors";
	}

	//return new details onto the wrapper to fake byref
	validatedAnswersWrapper.validatedAnswers = newValidatedAnswers;
	validatedAnswersWrapper.validationError = newValidationError;
	validatedAnswersWrapper.messageBoxText = newMessageBoxText;
	
	return bOK;
}

export const reducer = (state, action) => {
	state = state || initialState;

	if (action.type === dataFormRequestType) {
		return { ...state, isLoading: true, canRequest: false, error: undefined, validationError: undefined, undefined:undefined };
	}

	if (action.type === dataFormSuccessType) {
		return {
			...state, isLoading: false, canRequest: true, dataForm: action.dataForm, ownerId: action.ownerId, ownerType: action.ownerType, error: undefined,
			changedAnswers: {}, validatedAnswers: {}
		};
	}

	if (action.type === dataFormFailureType) {
		return { ...state, isLoading: false, canRequest: true, error: action.error};
	}

	//update changed answers
	if (action.type === dataFormUpdateChangedAnswerType) {
		return { ...state, changedAnswers: action.changedAnswers, validatedAnswers: action.validatedAnswers, validationError: action.validationError };
	}

	//validated error
	if (action.type === dataFormValidatedErrorType) {
		return { ...state, validatedAnswers: action.validatedAnswers, validationError: action.validationError, messageBoxText: action.messageBoxText };
	}

	//save
	if (action.type === dataFormSaveType) {
		return { ...state, isLoading: true, canRequest: false, error: undefined, hasSaveError: false, validatedAnswers: action.validatedAnswers };
	}

	//error saving
	if (action.type === dataFormSaveFailureType) {
		return { ...state, isLoading: false, canRequest: true, error: action.error, hasSaveError: true };
	}

	//remove message box text  
	if (action.type === dataFormRemoveMessageBoxTextType) {
		return { ...state, messageBoxText: undefined };
	}

	return state;
};